/**
 * What the Rôles & équipe screen offers this person, from their `/console/me`
 * features. The server checks every write again (ADR-0013 §2.2, see
 * server/connect-wallyo-service/console/team-rules.ts); the screen only hides
 * what is obviously refused and says why a grid cell is locked. Anything
 * subtler (acting upward, a role you couldn't grant) is left to the server,
 * whose refusal is shown with its message and request ID.
 */
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type {
  GetStaffMeResponse,
  MoneyLimit,
  RolePermissionGrant,
  StaffMemberListItem,
  StaffRoleListItem,
} from "@wallyo/contracts/console";
import { permissionsUnder, type DefinedPermissionId } from "@wallyo/contracts/permissions";
import { SETTINGS, type SettingCategory, type Sensitivity } from "@wallyo/contracts/settings";

const MANAGE_ROLES: StaffFeatureId = "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT";
const ASSIGN_ROLES: StaffFeatureId = "ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT";

/** One row of the grid: a grantable staff feature. */
export type GridFeature = { settingId: StaffFeatureId; sensitivity: Sensitivity };
export type GridGroup = { category: SettingCategory; features: GridFeature[] };

/** Every staff feature that can be granted, in SETTINGS order. Retired ones can't be granted. */
export const STAFF_FEATURES: readonly GridFeature[] = SETTINGS.flatMap((setting) =>
  setting.kind === "feature_enablement" &&
  setting.settingId.endsWith("_STAFF_FEATURE_ENABLEMENT") &&
  setting.status !== "retired"
    ? [{ settingId: setting.settingId as StaffFeatureId, sensitivity: setting.sensitivity }]
    : [],
);

/** The grid's rows grouped by category, in the order categories first appear in SETTINGS. */
export function featureGroups(features: readonly GridFeature[] = STAFF_FEATURES): GridGroup[] {
  const groups: GridGroup[] = [];
  const categoryOf = new Map(SETTINGS.map((setting) => [setting.settingId as string, setting.category]));
  for (const feature of features) {
    const category = categoryOf.get(feature.settingId)!;
    let group = groups.find((each) => each.category === category);
    if (!group) {
      group = { category, features: [] };
      groups.push(group);
    }
    group.features.push(feature);
  }
  return groups;
}

const CRITICAL: readonly StaffFeatureId[] = STAFF_FEATURES.filter((f) => f.sensitivity === "critical").map(
  (f) => f.settingId,
);

/** An Owner holds MANAGE_ROLES and every critical staff feature (team-rules.ts). */
export function isOwner(features: readonly StaffFeatureId[]): boolean {
  return features.includes(MANAGE_ROLES) && CRITICAL.every((feature) => features.includes(feature));
}

export type TeamAccess = {
  invite: boolean;
  deactivate: boolean;
  endSessions: boolean;
  /** Assign or remove roles (ASSIGN_ROLES). */
  assignRoles: boolean;
  /** Create and edit roles (MANAGE_ROLES). */
  manageRoles: boolean;
  /** Grant or revoke features on a role: MANAGE_ROLES, or ASSIGN_ROLES for low ones. */
  editGrid: boolean;
};

export function teamAccess(features: readonly StaffFeatureId[]): TeamAccess {
  const has = (feature: StaffFeatureId) => features.includes(feature);
  return {
    invite: has("INVITE_STAFF_STAFF_FEATURE_ENABLEMENT"),
    deactivate: has("DEACTIVATE_STAFF_STAFF_FEATURE_ENABLEMENT"),
    endSessions: has("END_STAFF_SESSIONS_STAFF_FEATURE_ENABLEMENT"),
    assignRoles: has(ASSIGN_ROLES),
    manageRoles: has(MANAGE_ROLES),
    editGrid: has(MANAGE_ROLES) || has(ASSIGN_ROLES),
  };
}

export type RowControls = { roles: boolean; endSessions: boolean; deactivate: boolean; reactivate: boolean };

/**
 * The buttons on one member's row. Nobody changes their own roles or
 * deactivates themselves, so your own row has neither. Ending your own
 * sessions is allowed (it signs you out). A deactivated member can't be acted on.
 */
export function rowControls(me: GetStaffMeResponse, member: StaffMemberListItem): RowControls {
  const access = teamAccess(me.features);
  const self = member.staffMemberId === me.staffMember.staffMemberId;
  const live = member.status !== "deactivated";
  return {
    roles: access.assignRoles && live && !self,
    endSessions: access.endSessions && live && member.openSessions > 0,
    deactivate: access.deactivate && live && !self,
    // The undo (WALLIYO-0121), offered only on a row that is deactivated, and
    // to whoever could have deactivated them. Not on your own row, because you
    // cannot be deactivated and reading it there would be nonsense.
    reactivate: access.deactivate && !live && !self,
  };
}

/** Why a grid cell can't be changed by this person, or null when it can. */
export type CellLock = "readOnly" | "ownRole" | "critical" | "high" | null;

/**
 * Mirrors ownRoleRefusal and grantRefusal: nobody changes what a role they
 * hold grants; low needs MANAGE_ROLES or ASSIGN_ROLES; high needs MANAGE_ROLES
 * and holding the feature; critical needs an Owner.
 */
export function cellLock(me: GetStaffMeResponse, role: StaffRoleListItem, feature: GridFeature): CellLock {
  const features = me.features;
  if (!teamAccess(features).editGrid) return "readOnly";
  if (me.staffMember.roles.some((held) => held.staffRoleId === role.staffRoleId)) return "ownRole";
  switch (feature.sensitivity) {
    case "critical":
      return isOwner(features) ? null : "critical";
    case "high":
      return features.includes(MANAGE_ROLES) && features.includes(feature.settingId) ? null : "high";
    default:
      return null;
  }
}

/** Does this role grant this feature? */
export function roleGrants(role: StaffRoleListItem, settingId: StaffFeatureId): boolean {
  return role.features.some((grant) => grant.settingId === settingId);
}

// ── The permissions under a feature (ADR-0019) ───────────────────────────

/** One nested row of the grid: a permission that sits on the feature above it. */
export type GridPermission = {
  settingId: DefinedPermissionId;
  feature: StaffFeatureId;
  sensitivity: Sensitivity;
  /** Whether it can carry limits at all. */
  limited: boolean;
  /** Whether someone without it can ask instead of being refused. */
  approvable: boolean;
};

/** The permissions that sit on one feature, in catalog order. */
export function permissionRows(settingId: StaffFeatureId): GridPermission[] {
  return permissionsUnder(settingId).map((permission) => ({
    settingId: permission.settingId as DefinedPermissionId,
    feature: permission.feature,
    sensitivity: permission.sensitivity,
    limited: permission.limit !== null,
    approvable: permission.approvable,
  }));
}

/** What this role grants for this permission, or null when it doesn't. */
export function rolePermission(role: StaffRoleListItem, settingId: DefinedPermissionId): RolePermissionGrant | null {
  return role.permissions.find((grant) => grant.settingId === settingId) ?? null;
}

/** What this person holds for this permission, or null when they don't hold it. */
export function heldPermission(
  me: GetStaffMeResponse,
  settingId: DefinedPermissionId,
): { settingId: DefinedPermissionId; limits: readonly MoneyLimit[] } | null {
  return me.permissions.find((held) => held.settingId === settingId) ?? null;
}

/**
 * Why a permission cell can't be changed, or null when it can. `featureFirst`:
 * the role doesn't grant the feature this permission sits on, so there is
 * nothing to grant it under (the server refuses with `feature_not_granted`).
 */
export type PermissionLock = CellLock | "featureFirst";

/**
 * Mirrors permissionGrantRefusal, read for the permission: low needs
 * MANAGE_ROLES or ASSIGN_ROLES; high needs MANAGE_ROLES and holding that
 * permission; critical needs an Owner who holds it; nobody changes a role they hold.
 */
export function permissionCellLock(
  me: GetStaffMeResponse,
  role: StaffRoleListItem,
  permission: GridPermission,
): PermissionLock {
  const features = me.features;
  if (!teamAccess(features).editGrid) return "readOnly";
  if (me.staffMember.roles.some((held) => held.staffRoleId === role.staffRoleId)) return "ownRole";
  if (permission.sensitivity === "critical") {
    const canGrant = isOwner(features) && heldPermission(me, permission.settingId) !== null;
    if (!canGrant) return "critical";
  }
  if (permission.sensitivity === "high") {
    const canGrant = features.includes(MANAGE_ROLES) && heldPermission(me, permission.settingId) !== null;
    if (!canGrant) return "high";
  }
  // Granted already: it can still be re-limited or revoked, feature or no feature.
  if (!rolePermission(role, permission.settingId) && !roleGrants(role, permission.feature)) return "featureFirst";
  return null;
}
